import {
  OFFER_CARGO_VEHICLE_SHAPES_LIST,
  PACKING_STYLES_LIST,
} from "@/shared/constants/offer-cargo";
import { fromIso8601Date, toLocaleTextAsDate } from "@/shared/lib/datetime";
import { SearchParamChipList } from "@/widgets/search-param-chip-list";
import { useMemo } from "react";
import type React from "react";
import type { CarrierCargoListPageProps } from "../model";

export type CarrierCargoListSearchParamKeys =
  | "loadingOn"
  | "loadingPrefecture"
  | "unloadingOn"
  | "unloadingPrefecture"
  | "packingStyle"
  | "vehicleShape"
  | "shippingFee"
  | "isNew"
  | "isNotProposedYet";

export type OfferCargoSearchParamChipListProps = {
  getDraftOffersCargosParams: CarrierCargoListPageProps["getDraftOffersCargosParams"];
  isNewFilter: boolean | undefined;
  onDelete: (values: CarrierCargoListSearchParamKeys) => void;
};

export const OfferCargoSearchParamChipList: React.FC<
  OfferCargoSearchParamChipListProps
> = (props) => {
  const params = useMemo(() => {
    const result: {
      value: CarrierCargoListSearchParamKeys;
      label: string;
    }[] = [];
    const { loadingOnFrom, loadingOnTo, loadingPrefecture } =
      props.getDraftOffersCargosParams ?? {};
    if (loadingOnFrom || loadingOnTo) {
      const loadingOnFromText = loadingOnFrom
        ? toLocaleTextAsDate(fromIso8601Date(loadingOnFrom))
        : "";
      const loadingOnToText = loadingOnTo
        ? toLocaleTextAsDate(fromIso8601Date(loadingOnTo))
        : "";
      result.push({
        value: "loadingOn",
        label: `積日：${loadingOnFromText}〜${loadingOnToText}`,
      });
    }
    if (loadingPrefecture) {
      result.push({
        value: "loadingPrefecture",
        label: `積地(都道府県)：${loadingPrefecture}`,
      });
    }
    const { unloadingOnFrom, unloadingOnTo, unloadingPrefecture } =
      props.getDraftOffersCargosParams ?? {};
    if (unloadingOnFrom || unloadingOnTo) {
      const unloadingOnFromText = unloadingOnFrom
        ? toLocaleTextAsDate(fromIso8601Date(unloadingOnFrom))
        : "";
      const unloadingOnToText = unloadingOnTo
        ? toLocaleTextAsDate(fromIso8601Date(unloadingOnTo))
        : "";
      result.push({
        value: "unloadingOn",
        label: `卸日：${unloadingOnFromText}〜${unloadingOnToText}`,
      });
    }
    if (unloadingPrefecture) {
      result.push({
        value: "unloadingPrefecture",
        label: `卸地(都道府県)：${unloadingPrefecture}`,
      });
    }

    // These properties are not in the GetOffersCargosParams type but are used in the UI
    // We need to cast to unknown to access them
    const params = props.getDraftOffersCargosParams as Record<string, unknown>;

    if (params?.packingStyle) {
      const packingStyleItem = PACKING_STYLES_LIST.find(
        (item) => item.value === params.packingStyle,
      );
      result.push({
        value: "packingStyle",
        label: `荷姿：${packingStyleItem?.label || params.packingStyle}`,
      });
    }

    if (params?.vehicleShape) {
      const vehicleShapeItem = OFFER_CARGO_VEHICLE_SHAPES_LIST.find(
        (item) => item.value === params.vehicleShape,
      );
      result.push({
        value: "vehicleShape",
        label: `ボディタイプ：${vehicleShapeItem?.label || params.vehicleShape}`,
      });
    }

    if (params?.shippingFee) {
      result.push({
        value: "shippingFee",
        label: `運賃：${params.shippingFee}円以上`,
      });
    }

    if (props.isNewFilter) {
      result.push({
        value: "isNew",
        label: "新着のみ",
      });
    }
    return result;
  }, [props.getDraftOffersCargosParams, props.isNewFilter]);
  if (params.length === 0) {
    return null;
  }
  const handleOnDelete = (value: CarrierCargoListSearchParamKeys) => {
    props.onDelete(value);
  };
  return <SearchParamChipList params={params} onDelete={handleOnDelete} />;
};
